import { useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ChatEntity, ChatRole, db, MessageEntity } from "../db";
import { postAPI } from "../utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

// Module-level to survive React Strict Mode double-mounting
// This prevents duplicate fetches when component remounts
let globalFetchStarted = false;

interface ResponseDocument {
    reference_id: string;
    title: string;
    source: string;
    data_source_type: string;
    page_number: number;
}

interface ResponseMessage {
    question: string;
    answer: string;
    response_id: string;
    timestamp: Date | string | number;
    documents: ResponseDocument[];
}

interface ResponseChat {
    conversation_id: string;
    messages: ResponseMessage[];
}

function toEpoch(ts: Date | string | number): number {
    if (ts instanceof Date) return ts.getTime();
    const d = new Date(ts as string);
    const t = d.getTime();
    return Number.isNaN(t) ? Date.now() : t;
}

function mergeReferencesByReferenceId(messages: MessageEntity[]): MessageEntity[] {
    for (const msg of messages) {
        if (!msg.references?.length) continue;
        const merged = new Map<string, (typeof msg.references)[0]>();
        for (const ref of msg.references) {
            const existing = merged.get(ref.reference_id);
            if (existing) {
                // Merge page numbers
                for (const page of ref.page_numbers) {
                    existing.page_numbers.add(page);
                }
            } else {
                merged.set(ref.reference_id, { ...ref, page_numbers: new Set(ref.page_numbers) });
            }
        }
        msg.references = [...merged.values()];
    }
    return messages;
}

/**
 * Hook to load chat history from backend and sync to IndexedDB.
 * Used by both main Layout and CopilotLayout to ensure consistent behavior.
 *
 * @param options.enabled - Whether to enable loading (default: true)
 * @param options.maxConversations - Max conversations to fetch (default: 100)
 */
export function useLoadChatHistory(options?: { enabled?: boolean; maxConversations?: number }) {
    const { enabled = true, maxConversations = 100 } = options || {};
    const { instance } = useMsal();
    const hasFetchedConversations = useRef(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!enabled) {
            setIsLoading(false);
            return;
        }
        // Use module-level guard to prevent duplicate fetches across React Strict Mode remounts
        if (globalFetchStarted || hasFetchedConversations.current) {
            setIsLoading(false);
            return;
        }

        globalFetchStarted = true;
        hasFetchedConversations.current = true;

        (async () => {
            try {
                const response = await postAPI({
                    url: `${BACKEND_URL}/v1/conversations/`,
                    payload: {
                        max_conversations: maxConversations,
                    },
                    instance,
                });

                if (response.status !== 200) {
                    console.warn(
                        "[useLoadChatHistory] Failed to fetch conversations:",
                        response.status,
                    );
                    setIsLoading(false);
                    return;
                }

                const storedChats = await db.chats.orderBy("createdAt").reverse().toArray();
                const responseChats = (await response.json()) as Array<ResponseChat>;
                const updatedMessages: MessageEntity[] = [];
                const updatedChats: ChatEntity[] = [];

                for (const chat of responseChats) {
                    const record = new ChatEntity();
                    const conversation_id = chat.conversation_id;
                    const responseMessages = chat.messages;

                    record.id = conversation_id;
                    record.description = `Chat-${record.id.slice(0, 8)}`;
                    record.totalTokens = 0;
                    record.vectorIndex = "combined";

                    if (responseMessages?.length) {
                        const earliest = Math.min(
                            ...responseMessages.map((m) => toEpoch(m.timestamp)),
                        );
                        record.createdAt = earliest;
                    } else {
                        record.createdAt = Date.now();
                    }

                    // Preserve suggested messages from local storage
                    const stored = storedChats.find((s) => s.id === conversation_id);
                    record.suggestedMessages = stored ? stored.suggestedMessages : [];
                    updatedChats.push(record);

                    for (const message of responseMessages) {
                        const userMessage = new MessageEntity();
                        userMessage.id = uuidv4();
                        userMessage.chatId = conversation_id;
                        userMessage.content = message.question;
                        userMessage.role = ChatRole.USER;
                        userMessage.createdAt = toEpoch(message.timestamp);
                        userMessage.references = [];
                        userMessage.toolCall = false;

                        const assistantMessage = new MessageEntity();
                        assistantMessage.id = uuidv4();
                        assistantMessage.chatId = conversation_id;
                        assistantMessage.responseId = message.response_id;
                        assistantMessage.content = message.answer;
                        assistantMessage.role = ChatRole.ASSISTANT;
                        assistantMessage.createdAt = toEpoch(message.timestamp) + 500;
                        assistantMessage.repliedId = userMessage.id;
                        assistantMessage.references = message.documents.map((doc) => ({
                            reference_id: doc.reference_id,
                            title: doc.title,
                            source: doc.source,
                            dataSourceType: doc.data_source_type,
                            page_numbers: new Set([doc.page_number]),
                        }));
                        assistantMessage.toolCall = false;
                        assistantMessage.pretrained = "";
                        assistantMessage.hasError = false;
                        assistantMessage.error = "";
                        assistantMessage.suggestions = [];

                        updatedMessages.push(userMessage);
                        updatedMessages.push(assistantMessage);
                    }
                }

                const mergedMessages = mergeReferencesByReferenceId(updatedMessages);

                // Preserve locally-created chats that don't exist in backend
                // This prevents wiping new chats created while the fetch was in progress
                const existingChats = await db.chats.toArray();
                const existingMessages = await db.messages.toArray();
                const backendChatIds = new Set(updatedChats.map((c) => c.id));

                // Keep chats that exist locally but not in backend (newly created during fetch)
                const localOnlyChats = existingChats.filter((c) => !backendChatIds.has(c.id));
                const localOnlyChatIds = new Set(localOnlyChats.map((c) => c.id));
                const localOnlyMessages = existingMessages.filter((m) =>
                    localOnlyChatIds.has(m.chatId as string),
                );

                await db.transaction("rw", db.chats, db.messages, async () => {
                    await db.chats.clear();
                    await db.messages.clear();
                    await db.chats.bulkPut([...updatedChats, ...localOnlyChats]);
                    await db.messages.bulkPut([...mergedMessages, ...localOnlyMessages]);
                });

                console.info(
                    "[useLoadChatHistory] Loaded",
                    updatedChats.length,
                    "conversations from backend,",
                    localOnlyChats.length,
                    "local-only chats preserved",
                );
                setIsLoading(false);
            } catch (error) {
                console.warn("[useLoadChatHistory] Error loading chat history:", error);
                setIsLoading(false);
            }
        })();
    }, [enabled, instance, maxConversations]);

    return { hasFetched: hasFetchedConversations.current, isLoading };
}
